import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  makeStyles,
  InputAdornment,
  Divider
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: "800px",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 600,
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
}));

// Rótulos traduzidos na renderização (pedidos.status.<valor>); o valor é o que vai para o backend.
const statusOptions = [
  { value: "pendente" },
  { value: "confirmado" },
  { value: "em_producao" },
  { value: "enviado" },
  { value: "entregue" },
  { value: "cancelado" },
];

const PedidoModal = ({ open, onClose, pedidoId, onSave }) => {
  const classes = useStyles();

  const [clientes, setClientes] = useState([]);
  const [formData, setFormData] = useState({
    cliente_id: "",
    data_pedido: new Date().toISOString().split("T")[0],
    data_entrega: "",
    status: "pendente",
    valor_desconto: 0,
    valor_acrescimo: 0,
    observacao: "",
    tipo_pedido: "",
    forma_pagamento: "",
  });

  const [itens, setItens] = useState([
    {
      item_numero: 1,
      descricao: "",
      codigo_produto: "",
      quantidade: 1,
      unidade: "UN",
      valor_unitario: 0,
      valor_desconto: 0,
    },
  ]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClientes();
  }, []);

  useEffect(() => {
    if (pedidoId && open) {
      fetchPedido();
    } else if (!pedidoId && open) {
      resetForm();
    }
  }, [pedidoId, open]);

  const fetchClientes = async () => {
    try {
      const { data } = await api.get("/clientes", {
        params: { pageNumber: 1, searchParam: "" },
      });
      setClientes(data.clientes || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchPedido = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/pedidos/${pedidoId}`);
      
      setFormData({
        cliente_id: data.cliente_id,
        data_pedido: data.data_pedido ? data.data_pedido.split("T")[0] : "",
        data_entrega: data.data_entrega ? data.data_entrega.split("T")[0] : "",
        status: data.status,
        valor_desconto: data.valor_desconto || 0,
        valor_acrescimo: data.valor_acrescimo || 0,
        observacao: data.observacao || "",
        tipo_pedido: data.tipo_pedido || "",
        forma_pagamento: data.forma_pagamento || "",
      });

      if (data.itens && data.itens.length > 0) {
        setItens(data.itens);
      }

      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      data_pedido: new Date().toISOString().split("T")[0],
      data_entrega: "",
      status: "pendente",
      valor_desconto: 0,
      valor_acrescimo: 0,
      observacao: "",
      tipo_pedido: "",
      forma_pagamento: "",
    });
    setItens([
      {
        item_numero: 1,
        descricao: "",
        codigo_produto: "",
        quantidade: 1,
        unidade: "UN",
        valor_unitario: 0,
        valor_desconto: 0,
      },
    ]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItens = [...itens];
    newItens[index][field] = value;
    setItens(newItens);
  };

  const addItem = () => {
    setItens([
      ...itens,
      {
        item_numero: itens.length + 1,
        descricao: "",
        codigo_produto: "",
        quantidade: 1,
        unidade: "UN",
        valor_unitario: 0,
        valor_desconto: 0,
      },
    ]);
  };

  const removeItem = (index) => {
    if (itens.length === 1) {
      toast.warning(i18n.t("pedidos.modal.toasts.minOneItem"));
      return;
    }
    const newItens = itens.filter((_, i) => i !== index);
    setItens(newItens.map((item, i) => ({ ...item, item_numero: i + 1 })));
  };

  const calcularValorItem = (item) => {
    const quantidade = parseFloat(item.quantidade) || 0;
    const valorUnitario = parseFloat(item.valor_unitario) || 0;
    const desconto = parseFloat(item.valor_desconto) || 0;
    return quantidade * valorUnitario - desconto;
  };

  const calcularSubtotal = () => {
    return itens.reduce((total, item) => total + calcularValorItem(item), 0);
  };

  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    const desconto = parseFloat(formData.valor_desconto) || 0;
    const acrescimo = parseFloat(formData.valor_acrescimo) || 0;
    return subtotal - desconto + acrescimo;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.cliente_id) {
        toast.error(i18n.t("pedidos.modal.toasts.selectClient"));
        return;
      }

      if (itens.length === 0) {
        toast.error(i18n.t("pedidos.modal.toasts.addItem"));
        return;
      }

      const hasEmptyDescription = itens.some((item) => !item.descricao);
      if (hasEmptyDescription) {
        toast.error(i18n.t("pedidos.modal.toasts.fillDescriptions"));
        return;
      }

      setLoading(true);

      const pedidoData = {
        ...formData,
        itens: itens.map((item) => ({
          ...item,
          valor_total: calcularValorItem(item),
        })),
      };

      if (pedidoId) {
        const { data } = await api.put(`/pedidos/${pedidoId}`, pedidoData);
        toast.success(i18n.t("pedidos.modal.toasts.updated"));
        onSave(data);
      } else {
        const { data } = await api.post("/pedidos", pedidoData);
        toast.success(i18n.t("pedidos.modal.toasts.created"));
        onSave(data);
      }

      setLoading(false);
      onClose();
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        {pedidoId ? i18n.t("pedidos.modal.title.edit") : i18n.t("pedidos.modal.title.add")}
        <IconButton
          aria-label={i18n.t("common.close")}
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("pedidos.modal.sections.orderData")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) =>
                  option.NOMECLI || option.NOMEFANTASIA || ""
                }
                value={
                  clientes.find((c) => c.CODCLI === formData.cliente_id) || null
                }
                onChange={(e, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    cliente_id: newValue?.CODCLI || "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("pedidos.modal.fields.client")}
                    variant="outlined"
                    required
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label={i18n.t("pedidos.modal.fields.orderDate")}
                name="data_pedido"
                type="date"
                value={formData.data_pedido}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label={i18n.t("pedidos.modal.fields.deliveryDate")}
                name="data_entrega"
                type="date"
                value={formData.data_entrega}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("common.status")}
                name="status"
                select
                value={formData.status}
                onChange={handleChange}
                variant="outlined"
                fullWidth
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {i18n.t(`pedidos.status.${option.value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("pedidos.modal.fields.orderType")}
                name="tipo_pedido"
                value={formData.tipo_pedido}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                placeholder={i18n.t("pedidos.modal.fields.orderTypePlaceholder")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("pedidos.modal.fields.paymentMethod")}
                name="forma_pagamento"
                value={formData.forma_pagamento}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                placeholder={i18n.t("pedidos.modal.fields.paymentMethodPlaceholder")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={i18n.t("common.notes")}
                name="observacao"
                value={formData.observacao}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </div>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("pedidos.modal.sections.items")}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 50 }}>#</TableCell>
                  <TableCell>{i18n.t("common.description")}</TableCell>
                  <TableCell style={{ width: 120 }}>{i18n.t("pedidos.modal.items.code")}</TableCell>
                  <TableCell style={{ width: 100 }}>{i18n.t("pedidos.modal.items.quantity")}</TableCell>
                  <TableCell style={{ width: 80 }}>{i18n.t("pedidos.modal.items.unit")}</TableCell>
                  <TableCell style={{ width: 120 }}>{i18n.t("pedidos.modal.items.unitPrice")}</TableCell>
                  <TableCell style={{ width: 120 }}>{i18n.t("pedidos.modal.fields.discount")}</TableCell>
                  <TableCell style={{ width: 120 }}>{i18n.t("common.total")}</TableCell>
                  <TableCell style={{ width: 60 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        value={item.descricao}
                        onChange={(e) =>
                          handleItemChange(index, "descricao", e.target.value)
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.codigo_produto}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "codigo_produto",
                            e.target.value
                          )
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantidade}
                        onChange={(e) =>
                          handleItemChange(index, "quantidade", e.target.value)
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ min: 0, step: 0.001 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.unidade}
                        onChange={(e) =>
                          handleItemChange(index, "unidade", e.target.value)
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.valor_unitario}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "valor_unitario",
                            e.target.value
                          )
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.valor_desconto}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "valor_desconto",
                            e.target.value
                          )
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(calcularValorItem(item))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeItem(index)}
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            startIcon={<AddIcon />}
            onClick={addItem}
            className={classes.addButton}
            variant="outlined"
            color="primary"
          >
            {i18n.t("pedidos.modal.buttons.addItem")}
          </Button>
        </div>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("pedidos.modal.sections.totals")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("pedidos.modal.fields.subtotal")}
                value={formatCurrency(calcularSubtotal())}
                variant="outlined"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("pedidos.modal.fields.discount")}
                name="valor_desconto"
                type="number"
                value={formData.valor_desconto}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("pedidos.modal.fields.surcharge")}
                name="valor_acrescimo"
                type="number"
                value={formData.valor_acrescimo}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={i18n.t("pedidos.modal.fields.total")}
                value={formatCurrency(calcularTotal())}
                variant="outlined"
                fullWidth
                disabled
                InputProps={{
                  style: { fontWeight: 600, fontSize: "1.2rem" },
                }}
              />
            </Grid>
          </Grid>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={<SaveIcon />}
        >
          {pedidoId ? i18n.t("pedidos.modal.buttons.update") : i18n.t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PedidoModal;
